import React, { useEffect, useMemo, useState } from "react";
import { Table, TableRow } from "../../../components/Table";
import { Button, Input } from "reactstrap";
import ToggleSwitch from "../../../components/Custom/Forms/ToggleSwitch/ToggleSwitch";
import { onFixed, responseToaster, seperator } from "../../../helperFunctions";
import StatePayInPaymentMethods from "./StatePayInPaymentMethods";
import StatePayInLevels from "./StatePayInLevels";
import StatePayInAmount from "./StatePayInAmount";
import { useDispatch } from "react-redux";
import { DeleteStatePayInMeta, UpdateStatePayInMetaStatus } from "../../../redux/slices/statePayInMetaSlice";
import DeleteModal from "../../../components/Custom/Modals/DeleteModal/DeleteModal";
import { useRole } from "../../../redux/slices/authSlice";

const StateBanks = ({ banks, onGet }) => {
    const dispatch = useDispatch();
    const role = useRole("State Wise Meta");
    const [filterColumns, setFilterColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [updateDetails, setUpdateDetails] = useState({});
    const [deleteData, setDeleteData] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const onDelete = async () => {
        try {
            setIsButtonLoading(true);
            const res = await dispatch(DeleteStatePayInMeta(deleteData)).unwrap();
            responseToaster(res);
            onGet();
            setDeleteData(false);
            setIsButtonLoading(false);
        } catch (err) {
            setDeleteData(false);
            setIsButtonLoading(false);
        }
    };

    const columns = [
        {
            title: "MMPID",
            name: "id",
        },
        {
            title: "PG",
            name: "pg_name",
            Cell: (data) => {
                return (
                    <div className="cell-order">
                        <div className="order-singal-div">
                            <p>
                                {data?.pg_name} {data?.pg_id ? `(${data?.pg_id})` : ""}
                            </p>
                        </div>
                        {data?.bank_details?.label ? (
                            <div className="order-singal-div">
                                <p>
                                    <span>Label : </span>
                                    {data?.bank_details?.label || ""}
                                </p>
                            </div>
                        ) : null}
                        <div className="order-singal-div">
                            <p>
                                <span>Type: </span>
                                {data?.pg_type || ""}
                            </p>
                        </div>
                        <div className="order-singal-div">
                            <p style={{ color: "var(--chatlook-darkblue--)" }}>
                                {data?.is_seamless === 1 ? "Seamless" : "Hosted"}
                            </p>
                        </div>
                    </div>
                )
            }
        },
        {
            title: "PAYMENT METHOD",
            name: "payment_method",
            Cell: (data) => (
                <div className="order-singal-div">
                    <p>{data?.payment_method}</p>
                </div>
            ),
        },
        {
            title: "PAYMENT APP",
            name: "payment_app",
            Cell: (data) => {
                return (
                    <StatePayInPaymentMethods
                        data={data}
                        onGet={onGet}
                        disabled={!role.includes("update-state-wise-meta")}
                    />
                )
            }
        },
        {
            title: "LEVEL",
            name: "level",
            Cell: (data) => {
                return (
                    <StatePayInLevels
                        data={data}
                        onGet={onGet}
                        disabled={!role.includes("update-state-wise-meta")}
                    />
                )
            }
        },
        {
            title: "ACTIVE",
            name: "is_active",
            Cell: (data) => (
                <ToggleSwitch
                    value={data?.is_active}
                    disabled={!role.includes("update-state-wise-meta")}
                    onChange={async (value) => {
                        try {
                            setIsLoading(true);
                            const payload = {
                                stateId: data?.state_id,
                                value: value,
                                pk: data?.pg_id,
                                pgName: data?.pg_name,
                                id: data?.id,
                                merchantId: data?.merchant_id
                            };
                            const res = await dispatch(UpdateStatePayInMetaStatus(payload)).unwrap();
                            responseToaster(res);
                            onGet();
                            setIsLoading(false);
                        } catch (err) {
                            setIsLoading(false);
                        }
                    }}
                />
            )
        },
        {
            title: "AMOUNT",
            name: "amount",
            Cell: (data) => (
                <StatePayInAmount
                    data={data}
                    onGet={onGet}
                    updateDetails={updateDetails}
                    setUpdateDetails={setUpdateDetails}
                    setIsLoading={setIsLoading}
                    disabled={!role.includes("update-state-wise-meta")}
                />
            )
        },
        {
            title: "DATE",
            name: "created_at_ist"
        },
        {
            title: "ACTION",
            name: "action",
            Cell: (data) => (
                <h6 className="mb-0">
                    <Button
                        color="danger"
                        className="btn-smallsize"
                        onClick={() => {
                            setDeleteData({
                                stateId: data?.state_id,
                                pgName: data?.pg_name,
                                id: data?.id,
                                pgId: data?.pg_id,
                                paymentMethod: data?.payment_method,
                                merchantId: data?.merchant_id
                            });
                        }}
                    >
                        <i className="fa-solid fa-trash"></i>
                    </Button>
                </h6>
            )
        },

    ];

    const filteredColumns = useMemo(() => {
        if (!role.includes("delete-state-wise-meta")) {
            return columns.filter(col => col.name !== "action");
        }
        return columns;
    }, [role, updateDetails]);

    useEffect(() => {
        setFilterColumns(filteredColumns.map(c => c.name));
    }, [filteredColumns]);

    return (
        <div>
            <h5>Bank Details</h5>
            <Table
                columns={filteredColumns}
                filterColumns={filterColumns}
                data={banks}
                isData={!!banks?.length}
                count={banks?.length || 0}
                isExpandable={false}
                minHeight={100}
                isLoading={isLoading}
            >
                {banks?.map((val, i) => (
                    <TableRow
                        key={i}
                        columns={filteredColumns}
                        filterColumns={filterColumns}
                        item={val}
                        isExpandable={false}
                    />
                ))}
            </Table>
            <DeleteModal
                isOpen={deleteData}
                onCancelClick={() => setDeleteData(false)}
                onDoneClick={onDelete}
                isButtonLoading={isButtonLoading}
                title="Delete Bank"
                content="Are you sure you want to delete this bank from state meta?"
            />
        </div>
    );
};

export default StateBanks;
